/**
 * Aufrufe an die Edge Functions.
 *
 * WARUM GUTHABEN NICHT AUS DER TABELLE KOMMT: `credit_ledger` ist für
 * `authenticated` gesperrt, und das ist Absicht. Die Verfallsregel — welches
 * Kontingent wann verfällt und wie der Rest ausgebucht wird — steht in
 * `ledger.ts` und soll an genau einer Stelle stehen. Eine zweite Summierung im
 * Browser wäre eine zweite Wahrheit über Geld, und die beiden driften genau
 * dann auseinander, wenn jemand hinsieht.
 *
 * `billing/status` legt nebenbei die Zeile in `public.accounts` an, falls es
 * sie noch nicht gibt (`resolveAccountForUser`). Es ist damit der erste
 * Aufruf, den ein frisch bestätigtes Konto braucht — vor allem anderen.
 */

import { SUPABASE_URL } from './config';
import { supabase } from './supabase';
import { mailSprache, sprache, t } from './sprache';

export interface Monat {
  from: string;
  credits_used: number;
  credit_cap: number | null;
  runs: number;
  eur_used: number;
  eur_cap: number | null;
}

export interface Guthaben {
  metered: boolean;
  balance: number | null;
  plan_allowance_remaining?: number;
  plan_allowance_expires_at?: string | null;
  pack_credits?: number;
  /** Davon aus gekauften Paketen mit Verfall (seit 27.09.2026). Der Rest von pack_credits: Gutschriften ohne Verfall. */
  pack_remaining?: number;
  /** Nächster Verfall eines gekauften Pakets (seit 27.09.2026). */
  pack_expires_at?: string | null;
  /** Startguthaben, das auf die bestätigte E-Mail wartet. */
  signup_credits_pending?: number;
  eur_rate_configured: boolean;
  month: Monat;
}

export interface Plan {
  plan: string;
  name: string;
  monthly_credits: number;
  price_label: string;
  /** Für dieses Konto bezahlbar (aktiv, oder im Mollie-Testmodus ein Testkonto). Fehlt bei einer billing-Function von vor dem 27.09.2026: dann waren nur buchbare Pläne in der Liste. */
  buchbar?: boolean;
  /** Höchstzahl eigener Zielgruppen, null unbegrenzt (seit 09.10.2026). Fehlt vor der Migration. */
  max_eigene_zielgruppen?: number | null;
  /** Berichte nur für eingeladene Adressen (seit 09.10.2026). Fehlt vor der Migration. */
  leserliste?: boolean;
  /** Eigene Interviews und Tickets hochladen (seit 21.10.2026, gespraeche.ts). Fehlt vor der Migration. */
  eigene_gespraeche?: boolean;
  /** Preis netto in Cent (seit 16.10.2026, #270). Fehlt bei einer billing-Function von davor. */
  preis_netto_cent?: number | null;
}

/** Was ohne Plan gilt, auch für Paketkäufer (plan_merkmale.ts, OHNE_PLAN). */
export interface OhnePlan {
  max_eigene_zielgruppen: number | null;
  leserliste: boolean;
  eigene_gespraeche?: boolean;
}

/** Ein Credit-Paket, einmal bezahlt, ohne Abo (seit 27.09.2026). */
export interface Paket {
  pack: string;
  name: string;
  credits: number;
  price_label: string;
  /** Leer: verfällt nicht (seit 06.10.2026). */
  valid_days: number | null;
  buchbar: boolean;
  /** Preis netto in Cent (seit 16.10.2026, #270). */
  preis_netto_cent?: number | null;
}

export interface Status {
  account_id: string;
  plan: string;
  subscription: {
    status: string; plan: string; current_period_end: string | null;
    /** Planwechsel, der mit der nächsten Abbuchung greift (Mollie, seit 07.10.2026). */
    ab_naechster_zahlung?: string | null;
  } | null;
  balance: Guthaben;
  plans: Plan[];
  /** Fehlt bei einer billing-Function von vor dem 09.10.2026. */
  ohne_plan?: OhnePlan;
  /** Fehlt bei einer billing-Function von vor dem 27.09.2026. */
  packs?: Paket[];
  /**
   * Erster angemeldeter Aufruf aus Claude (seit 26.09.2026). Fehlt das Feld,
   * laeuft die billing-Function noch ohne die Spalte: dann gilt die alte
   * Regel, siehe `istVerbunden`.
   */
  erster_aufruf_at?: string | null;
  /**
   * Karte hinterlegen gegen Credits (#155, seit 07.10.2026). Fehlt bei einer
   * billing-Function von davor; dann gibt es kein Angebot.
   */
  karte?: { credits: number; erhalten: boolean; angeboten: boolean };
  /** Rechnungsdaten (Mollie, seit 07.10.2026). null: noch keine eingetragen. */
  rechnungsdaten?: Rechnungsdaten | null;
  /** Steuerfall aus den Rechnungsdaten; `erlaubt: false` heißt: an diesen Kunden wird noch nicht verkauft. */
  steuer?: { satz: string; fall: string; erlaubt: boolean; grund: string | null } | null;
  /** Mollie läuft mit Testschlüssel: Zahlungen sind Testzahlungen. */
  testmodus?: boolean;
  /** Nur für Konten in SIM_ADMIN_KONTEN (seit 16.10.2026): dann gibt es den Reiter „Admin“. */
  admin?: boolean;
  /** Bestellungen, die sich gerade widerrufen lassen (#272, widerruf.ts). Fehlt bei einer billing-Function von davor. */
  widerrufbar?: Widerrufbar[];
  /** Widerrufe der letzten 60 Tage, mit Stand der Erstattung. */
  widerrufen?: Array<{ id: string; produkt_name: string; widerrufen_am: string; erstattet_cent: number | null; wertersatz_cent: number | null; in_bearbeitung: boolean }>;
  /** Newsletter (seit 26.10.2026, newsletter.ts): aus, offen (Bestätigung steht aus) oder an. Fehlt bei einer billing-Function von davor. */
  newsletter?: NewsletterStand;
}

export type NewsletterStand = 'aus' | 'offen' | 'an';

export interface Widerrufbar {
  id: string;
  art: 'plan' | 'paket';
  produkt_name: string;
  credits: number;
  brutto_cent: number;
  bestellt_am: string;
  /** Letzter Tag der Frist, YYYY-MM-DD (Berlin). */
  frist_bis: string;
}

export interface Rechnungsdaten {
  art: 'privat' | 'firma';
  name: string;
  firma?: string | null;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  ust_id?: string | null;
  ust_id_gueltig?: boolean;
  registernummer?: string | null;
}

export interface Rechnung {
  id: string;
  rechnungsnummer: string | null;
  brutto_cent: number | null;
  beschreibung: string | null;
  created_at: string;
}


export interface Lauf {
  id: string;
  created_at: string;
  set: string;
  /** Anzeigename der Zielgruppe; fehlt bei alten Servern, dann gilt der Slug. */
  set_name?: string | null;
  /** Erste Zeile des geprüften Texts, gekürzt (uebersicht.ts, auszug). */
  text?: string | null;
  asset: string;
  mode: 'compare' | 'feedback' | string;
  verdict: string;
  credits: number;
  personas: number;
  variant: string | null;
  model: string | null;
  /** Nur bei mode 'compare'. Bei 'feedback' lief kein Vergleich. */
  scores?: { candidate: number; baseline: number };
  margin?: number;
  /**
   * Stimmen und Rauschen, serverseitig mit denselben Schwellen wie in Claude
   * berechnet (`uebersicht.ts` → `render.ts`). `knapp` null heisst: die Zeile
   * stammt von vor der Spalte `wins_candidate`, es laesst sich nicht sagen.
   */
  stimmen?: { candidate: number; baseline: number } | null;
  knapp?: boolean | null;
  score_im_rauschen?: boolean | null;
  /** Gemeldetes Marktergebnis, falls es eines gibt. Nur bei Vergleichen. */
  ergebnis?: { gewinner: 'kandidat' | 'baseline' | 'unentschieden'; zaehlbar: boolean; treffer: boolean | null; grund: string };
  study: string | null;
  study_id: string | null;
  /** Projekt der Studie (projekt.ts). Fehlt bei alten Servern und bei Runs ohne Studie. */
  projekt?: string | null;
}

export interface PersonaSet {
  slug: string;
  name: string;
  market: string | null;
  segment: string | null;
  language: string | null;
  visibility: 'shared' | 'private' | string;
  persona_count: number;
  evidence_persona_count: number;
  evidence_backed: boolean;
  description: string | null;
  /**
   * Wen das Set darstellt (seit 26.09.2026, jury.ts im Backend). Bestimmt die
   * Rolle der Jury. Fehlt bei einer billing-Function von davor.
   */
  zielgruppe_art?: 'b2b' | 'b2c' | 'politik';
  /**
   * Belege gesamt und je Quellenart (Migration 20260927160000, beleglage.ts).
   * Fehlen vor der Migration; dann zeigt die Liste nur „x von y mit Belegen".
   */
  evidence_count?: number | string;
  evidence_by_type?: Record<string, number>;
}

/**
 * Fehler mit einem Text, der dem Nutzer etwas sagt. `abgemeldet` steuert die
 * Anzeige. `status` und `code` (das `error` aus der Antwort) erlauben einer
 * Seite, einen 404 selbst zu erklaeren.
 */
export class ApiFehler extends Error {
  constructor(
    message: string, readonly abgemeldet = false, readonly status?: number, readonly code?: string,
    /** Das Konto ist gesperrt (siehe `KONTO_GESPERRT`): „Noch einmal laden" bringt dann nichts. */
    readonly gesperrt = false,
    /** Maschinenlesbarer Grund aus der Antwort (`code`), z. B. `rechnungsdaten_fehlen`. */
    readonly kennung?: string,
  ) {
    super(message);
    this.name = 'ApiFehler';
  }
}

/** Ob ein erneuter Versuch etwas aendern kann. Nein bei gesperrtem Konto. */
export const nochmalSinnvoll = (e: unknown) => !(e instanceof ApiFehler && e.gesperrt);

/**
 * Zu der E-Mail-Adresse gibt es ein aelteres Konto mit Sonderrechten, das
 * `accounts_guard_privileged` nicht automatisch mit dem Login verknuepft
 * (Wurzel-AGENTS.md, „Ein privilegiertes Konto laesst sich nicht per
 * Registrierung uebernehmen"). Die billing-Function meldet das seit 28.09.2026
 * mit 409 und `code: "konto_gesperrt"`. Ein 503 mit `error: "Account
 * unavailable"` ist dagegen vorübergehend (seit 19.10.2026 mit
 * `code: "konto_voruebergehend"`): bis dahin stand auch dort dieser Satz, ohne
 * „Noch einmal laden", obwohl genau das geholfen hätte.
 */
export const KONTO_GESPERRT =
  'Zu deiner E-Mail-Adresse gibt es bei 99takes ein älteres Konto, das wir nicht automatisch mit deinem Login verbinden. Schreib uns an run@99takes.com, dann klären wir das.';
export const KONTO_GESPERRT_EN =
  'There is an older 99takes account for your email address that we do not connect to your login automatically. Write to us at run@99takes.com and we will sort it out.';

/**
 * Englische Sätze für Fehler, die der Server mit einem `code` schickt und die
 * die App anzeigt. Der Server antwortet deutsch; auf Englisch steht statt
 * seines Satzes dieser hier. Codes, die hier fehlen, kommen deutsch durch.
 * Wo der deutsche Satz Zahlen oder ein Datum trägt (Plangrenze, Frist), ist
 * der englische allgemeiner.
 */
const SERVER_CODES_EN: Record<string, string | ((koerper: Record<string, unknown>) => string)> = {
  rechnungsdaten_fehlen: 'Please enter your billing details first.',
  land_nicht_moeglich: 'Purchases are not possible for this country yet.',
  nicht_freigeschaltet: 'Purchases are not enabled yet.',
  bestellung_fehlt: 'Please order with the button “Order with obligation to pay”.',
  zustimmung_fehlt: 'Please confirm that 99takes should start right away. Without this consent we cannot add the credits right away.',
  plan_grenze: k => (k.plan_grenze === 'leserliste'
    ? 'Reports for invited addresses only are available from the Pro plan. In your plan the link stays open: anyone who has it can read the report.'
    : k.plan_grenze === 'eigene_gespraeche'
    ? 'Uploading your own interviews and tickets is available from the Pro plan.'
    : 'Your plan allows no more audiences of your own. Larger plans allow more, or change an existing audience.'),
  privilegiert: 'This account cannot be deleted in the app. Write to run@99takes.com.',
  bestaetigung: 'To confirm, enter the email address of the account.',
  abo: 'Your subscription could not be canceled. Nothing was deleted. Try again or write to run@99takes.com.',
  eigenes: 'You delete your own account under Account, not here.',
  admin: 'An admin account cannot be deleted here. Remove it from SIM_ADMIN_KONTEN first.',
  name_fehlt: 'Please enter your name.',
  adresse_fehlt: 'Please enter an email address for the confirmation.',
  schon_widerrufen: 'You have already withdrawn from this contract.',
  nicht_bezahlt: 'This order has not been paid yet. If the payment is canceled, no contract is formed.',
  frist_abgelaufen: 'The withdrawal period has ended. You can cancel a plan at any time.',
  angaben_fehlen: 'Please fill in all required fields.',
  zu_viele: 'Too many cancellations in a short time. You can also cancel by email to run@99takes.com.',
};

/** Der englische Satz zu einem Code des Servers, oder undefined, wenn die App keinen kennt. */
export function englischFuerCode(code: string | undefined, koerper: Record<string, unknown> = {}): string | undefined {
  const e = code ? SERVER_CODES_EN[code] : undefined;
  return typeof e === 'function' ? e(koerper) : e;
}

/** Satz des Servers, auf Englisch durch den bekannten Code ersetzt. */
const serverSatz = (error: string, code: string | undefined, koerper: Record<string, unknown>) =>
  t(error, englischFuerCode(code, koerper) ?? error);

/**
 * Aus einer Fehlerantwort der billing-Function wird ein Satz fuer Menschen.
 * Kein JSON und kein Rohtext in der Meldung: der Nutzer kann damit nichts
 * anfangen, und er landet so in Bildschirmfotos an den Support. Der Status
 * bleibt stehen, damit der Support etwas in der Hand hat; die Rohantwort geht
 * in die Konsole.
 */
export function fehlerAusAntwort(status: number, text: string): ApiFehler {
  let koerper: { error?: unknown; code?: unknown; [k: string]: unknown } | null = null;
  try { const j = JSON.parse(text); if (j && typeof j === 'object') koerper = j; } catch { /* kein JSON */ }
  const error = typeof koerper?.error === 'string' ? koerper.error : undefined;
  const code = typeof koerper?.code === 'string' ? koerper.code : undefined;

  if (code === 'konto_gesperrt') {
    return new ApiFehler(t(KONTO_GESPERRT, KONTO_GESPERRT_EN), false, status, error, true);
  }
  if (code === 'konto_voruebergehend') {
    return new ApiFehler(t(
      `Dein Konto ließ sich gerade nicht laden. Versuch es in einem Moment noch einmal. (HTTP ${status})`,
      `Your account could not be loaded just now. Try again in a moment. (HTTP ${status})`,
    ), false, status, error, false, code);
  }
  // Die Abrechnung antwortet mit einem deutschen Satz und einem Code, wenn der
  // Nutzer etwas tun kann (Rechnungsdaten fehlen, Land nicht möglich). Den Satz
  // zeigen wir, statt ihn hinter „nicht wie erwartet" zu verstecken.
  if (code && error && status >= 400 && status < 500) return new ApiFehler(serverSatz(error, code, koerper ?? {}), false, status, error, false, code);
  return new ApiFehler(t(
    `99takes hat gerade nicht wie erwartet geantwortet. Versuch es in einem Moment noch einmal. (HTTP ${status})`,
    `99takes did not respond as expected. Try again in a moment. (HTTP ${status})`,
  ), false, status, error);
}

async function bearer(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiFehler(t('Deine Sitzung ist abgelaufen.', 'Your session has expired.'), true);
  return token;
}

/**
 * Ein GET gegen die billing-Function. Einmal geschrieben statt je Route neu —
 * sonst steht die Unterscheidung „Sitzung abgelaufen" gegen „Netz weg" an drei
 * Stellen und driftet.
 */
async function hole<T>(route: string, init?: { method: 'POST'; body: unknown }): Promise<T> {
  const token = await bearer();
  let res: Response;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/billing/${route}`, {
      headers: { Authorization: `Bearer ${token}`, ...(init ? { 'Content-Type': 'application/json' } : {}) },
      ...(init ? { method: init.method, body: JSON.stringify(init.body) } : {}),
    });
  } catch {
    // Netz weg, Rechner im Schlaf, Firewall. Keine Fehlermeldung erfinden,
    // die nach einem Kontoproblem klingt.
    throw new ApiFehler(t('Keine Verbindung zu 99takes. Prüf deine Internetverbindung.', 'No connection to 99takes. Check your internet connection.'));
  }

  if (res.status === 401) throw new ApiFehler(t('Deine Sitzung ist abgelaufen. Melde dich noch einmal an.', 'Your session has expired. Please sign in again.'), true);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const f = fehlerAusAntwort(res.status, text);
    if (!f.gesperrt) console.warn(`[api] ${route}: HTTP ${res.status}`, text.slice(0, 500));
    throw f;
  }
  return (await res.json()) as T;
}

export const ladeStatus = () => hole<Status>('status');

/**
 * Mollie-Zahlung für ein Abo ({plan}) oder ein Paket ({pack}). Liefert die
 * Adresse, zu der der Browser wechselt. Nur aus der Bestellübersicht
 * (`Bestellung.tsx`): `bestellt` heißt „Zahlungspflichtig bestellen“ geklickt,
 * `sofort_beginn` das Kontrollkästchen für Verbraucher (§ 312j BGB, #273).
 */
export const starteCheckout = (was: ({ plan: string } | { pack: string }) & { bestellt: true; sofort_beginn: boolean }) =>
  // `sprache`: in welcher Sprache die Zustimmung erteilt wurde (der Server speichert es).
  hole<{ url: string }>('checkout', { method: 'POST', body: { ...was, sprache: sprache() } });

/** Widerruf einer Bestellung (§ 356a BGB, #272). */
export const widerrufe = (d: { bestellung: string; name: string; email: string }) =>
  hole<{ widerrufen: boolean; eingegangen_am: string; mail: string; wertersatz_cent: number | null; erstattung_cent: number | null; in_bearbeitung: boolean }>(
    'widerruf', { method: 'POST', body: d });

export interface KuendigungAngaben {
  name: string;
  konto_email?: string;
  vertrag: string;
  art: 'ordentlich' | 'ausserordentlich';
  grund?: string;
  zeitpunkt: string;
  bestaetigung_an: string;
}

export interface KuendigungAntwort {
  eingegangen: boolean;
  eingegangen_am: string;
  mail: string;
  /** Mit Anmeldung das Ergebnis, sonst nur „eingegangen“ (das Ergebnis kommt per Mail). */
  ergebnis: string;
  endet_am?: string | null;
}

/**
 * Kündigungsbutton (§ 312k BGB, #271): geht auch ohne Anmeldung. Mit Sitzung
 * schickt die App das Token mit, dann ordnet der Server die Kündigung dem
 * Konto zu. Ein abgelaufenes Token ist hier kein Fehler.
 */
export async function sendeKuendigung(d: KuendigungAngaben): Promise<KuendigungAntwort> {
  const { data } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
  const token = data.session?.access_token;
  let res: Response;
  try {
    res = await fetch(`${SUPABASE_URL}/functions/v1/billing/kuendigung`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      // `sprache`: für die Bestätigung, wenn das Konto unbekannt ist (kuendigung.ts).
      body: JSON.stringify({ ...d, sprache: mailSprache() }),
    });
  } catch {
    throw new ApiFehler(t(
      'Keine Verbindung zu 99takes. Prüf deine Internetverbindung, oder kündige per E-Mail an run@99takes.com.',
      'No connection to 99takes. Check your internet connection, or cancel by email to run@99takes.com.',
    ));
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let error = '';
    let koerper: Record<string, unknown> = {};
    try { koerper = JSON.parse(text) ?? {}; error = typeof koerper.error === 'string' ? koerper.error : ''; } catch { /* kein JSON */ }
    const code = typeof koerper.code === 'string' ? koerper.code : undefined;
    const ersatz = t(
      `Die Kündigung ließ sich gerade nicht senden (HTTP ${res.status}). Du kannst auch per E-Mail an run@99takes.com kündigen.`,
      `The cancellation could not be sent right now (HTTP ${res.status}). You can also cancel by email to run@99takes.com.`,
    );
    throw new ApiFehler(error ? serverSatz(error, code, koerper) : ersatz, false, res.status);
  }
  return (await res.json()) as KuendigungAntwort;
}

/** Karte bei Mollie hinterlegen (Zahlung über 0,00 €), nichts wird abgebucht. */
export const starteKarte = () => hole<{ url: string }>('karte', { method: 'POST', body: {} });

/** Rechnungsdaten speichern; die USt-ID prüft der Server bei VIES. */
export const speichereRechnungsdaten = (d: Partial<Rechnungsdaten>) =>
  hole<{ rechnungsdaten: Rechnungsdaten; steuer: NonNullable<Status['steuer']>; hinweis: string | null }>('rechnungsdaten', { method: 'POST', body: d });

/** Planwechsel, gilt ab der nächsten Abbuchung (Mollie rechnet nicht anteilig). */
export const wechslePlan = (plan: string) =>
  hole<{ plan: string; ab_naechster_zahlung: string | null; naechste_zahlung: string | null }>('plan', { method: 'POST', body: { plan } });

/** Abo kündigen: keine weitere Abbuchung, das bezahlte Kontingent gilt bis zum Periodenende. */
export const kuendige = () => hole<{ gekuendigt: boolean; gilt_bis: string | null }>('kuendigen', { method: 'POST', body: {} });

/** Konto ganz löschen (#274). Zur Bestätigung die E-Mail-Adresse des Kontos; ein laufendes Abo kündigt der Server vorher. */
export const loescheKonto = (email: string, newsletterAbmelden = false) =>
  hole<{ geloescht: boolean }>('konto-loeschen', { method: 'POST', body: { email, ...(newsletterAbmelden ? { newsletter_abmelden: true } : {}) } });

/** Newsletter an (schickt die Mail fürs Double-Opt-in) oder aus (newsletter.ts). */
export const setzeNewsletter = (an: boolean) =>
  hole<{ newsletter: NewsletterStand; mail?: 'verschickt' | 'schon_verschickt' | 'nicht_verschickt' }>('newsletter', { method: 'POST', body: { an } });

// ── Admin-Ansicht (admin.ts, seit 16.10.2026) ──────────────────────────────
// Die Routen antworten nur Konten in SIM_ADMIN_KONTEN, allen anderen mit 404.

export interface AdminKonto {
  id: string;
  email: string | null;
  name: string | null;
  role: string;
  admin: boolean;
  erstellt: string | null;
  erster_aufruf: string | null;
  anmeldung: boolean;
  bestaetigt: boolean;
  zuletzt_angemeldet: string | null;
  runs: number;
  letzter_run: string | null;
  studien: number;
  berichte: number;
  zielgruppen: number;
  schluessel: number;
  /** null: nicht abgerechnet (unbegrenzt). Fehlt: Ledger nicht lesbar. */
  guthaben?: number | null;
  abo: { plan: string | null; status: string | null } | null;
}

export interface AdminKontoDetail {
  konto: { id: string; email: string | null; name: string | null; plan: string | null; role: string; admin: boolean; erstellt: string | null; erster_aufruf: string | null };
  anmeldung: { bestaetigt: string | null; zuletzt: string | null; anbieter: string | null } | null;
  abrechnung: {
    unbegrenzt: boolean;
    guthaben?: number | null;
    notiz: string | null;
    abo: { plan: string | null; status: string | null; seit: string | null; bis: string | null; mollie: boolean; ab_naechster_zahlung: string | null } | null;
  };
  /** Gesamtzahlen; die Listen darunter tragen höchstens die letzten 50. */
  zaehlung?: { runs: number };
  ledger: Array<{ zeit: string; delta: number; grund: string; notiz: string | null; verfaellt: string | null }>;
  runs: Array<{ id: string; zeit: string; art: string | null; textart: string | null; credits: number; urteil: string | null; personas: number | null; modell: string | null; studie: string | null }>;
  zielgruppen: Array<{ slug: string; name: string; sichtbarkeit: string | null; erstellt: string | null; personas: number; kopie_von: string | null }>;
  studien: Array<{ id: string; name: string; erstellt: string | null; berichte: number; offene_links: number }>;
  schluessel: Array<{ praefix: string; name: string | null; erstellt: string | null; zuletzt: string | null; widerrufen: string | null }>;
}

export async function ladeAdminKonten(): Promise<AdminKonto[]> {
  return (await hole<{ konten: AdminKonto[] }>('admin-konten')).konten ?? [];
}

export const ladeAdminKonto = (id: string) => hole<AdminKontoDetail>(`admin-konto?id=${encodeURIComponent(id)}`);

/** Ein fremdes Konto löschen, zur Bestätigung dessen E-Mail-Adresse. Gleicher Ablauf wie loescheKonto. */
export const loescheFremdesKonto = (id: string, email: string) =>
  hole<{ geloescht: boolean; umfang?: { runs: number; studien: number; zielgruppen: number } }>('admin-konto-loeschen', { method: 'POST', body: { id, email } });

export async function ladeRechnungen(): Promise<Rechnung[]> {
  return (await hole<{ rechnungen: Rechnung[] }>('rechnungen')).rechnungen ?? [];
}

/** Frischer Link zum PDF einer Rechnung (Mollie-Links laufen ab). */
export const rechnungsLink = (id: string) => hole<{ url: string }>(`rechnung?id=${encodeURIComponent(id)}`);

export async function ladeLaeufe(): Promise<Lauf[]> {
  return (await hole<{ runs: Lauf[] }>('runs')).runs ?? [];
}

/** Projekt einer Studie setzen; '' nimmt sie aus dem Projekt. */
export const setzeStudienProjekt = (studyId: string, projekt: string) =>
  hole<{ study_id: string; projekt: string | null }>('studie-projekt', { method: 'POST', body: { study_id: studyId, projekt } });

/** Was eine Persona zur gepruefte Fassung sagt. Leere Teile sind null. */
export interface Rueckmeldung {
  persona: string;
  /** Note der geprueften Fassung, nur bei vollen Vergleichen. */
  note: number | null;
  /** Nur bei Laeufen ohne Vergleich: wuerde weitergehen. */
  weiter: boolean | null;
  einwand: string | null;
  unklar: string | null;
  vorschlag: string | null;
  absprung: string | null;
  /** Nur bei Randpersonas (kern-rand.ts). Fehlt bei allen anderen und bei alten Läufen. */
  rolle?: 'rand';
  /** Nur mit Markenversprechen: hält der Text es, mit einem Satz Begründung. */
  versprechen?: { haelt: boolean; grund: string | null };
}

/**
 * Ein Lauf im Detail (`billing/run?id=`, seit 26.09.2026). Nur Laeufe des
 * eigenen Kontos; welche Felder herausgehen, entscheidet `readRun` in
 * `uebersicht.ts`, nicht diese Seite.
 */
export interface LaufDetail extends Lauf {
  set_version: number | null;
  stufe: 'full' | 'quick';
  /** Beide Noten je Persona, Rohform wie im Bericht. Lesen mit `personaNoten`. */
  personen?: Array<{ persona: string; rolle?: 'rand'; geprueft: number; vergleich: number; gewinner: 'geprueft' | 'vergleich' | null }>;
  /** Nur wenn der Lauf Kern und Rand hatte. Dann gelten `stimmen`, `scores` und `verdict` für den Kern. Lesen mit `leseKernRand`. */
  kern_rand?: unknown;
  /** „Gefällt allen ein bisschen". Lesen mit `leseMuster`. */
  muster?: unknown;
  /** Nur mit Versprechen im Lauf. */
  markenversprechen?: { text: string; haelt: number; von: number };
  /** Der geprüfte Text stammt von write_challenger; gleiche_jury: aus den Einwänden genau dieses Sets geschrieben. */
  geschaerft?: { gleiche_jury: boolean };
  texte: {
    geprueft: string | null; vergleich: string | null; vergleich_erzeugt: boolean | null; quelle_url: string | null;
    /** Die Gliederung, wie die Personas sie gelesen haben (seit 27.10.2026). Fehlt bei älteren Runs und vor dem Deploy. */
    abschnitte_geprueft?: Array<{ rolle: string; text: string }> | null;
    abschnitte_vergleich?: Array<{ rolle: string; text: string }> | null;
    /** abschnitte: vom Aufrufer geliefert; html, text: daraus gegliedert. */
    gliederung_geprueft?: 'abschnitte' | 'html' | 'text' | null;
    gliederung_vergleich?: 'abschnitte' | 'html' | 'text' | null;
  };
  rueckmeldungen: Rueckmeldung[];
}

export const ladeLauf = (id: string) => hole<LaufDetail>(`run?id=${encodeURIComponent(id)}`);

export async function ladeSets(): Promise<PersonaSet[]> {
  return (await hole<{ sets: PersonaSet[] }>('sets')).sets ?? [];
}

export interface BerichtEintrag {
  id: string;
  study: string | null;
  created_at: string;
  variants: number;
  set: string | null;
  /** Nur solange der Link gilt. */
  url: string | null;
  shared: boolean;
  /** Link auf ein eigenes privates Set, ausdruecklich freigegeben. */
  shared_by_owner: boolean;
  revoked_at: string | null;
  /** Leer: jeder mit dem Link. Sonst nur diese Adressen, mit Code per Mail. Fehlt bei aelteren Servern. */
  leser?: string[];
}

export async function ladeBerichte(): Promise<BerichtEintrag[]> {
  return (await hole<{ reports: BerichtEintrag[] }>('reports')).reports ?? [];
}

/** Die eingefrorene Nutzlast eines eigenen Berichts — auch ohne Link. */
export const ladeEigenenBericht = (id: string) => hole<any>(`report?id=${encodeURIComponent(id)}`);

export const zieheLinkZurueck = (id: string) =>
  hole<{ id: string; revoked_at: string }>('report-revoke', { method: 'POST', body: { id } });

/** Leserliste eines Berichts ersetzen. Leere Liste: der Link ist wieder offen. */
export const setzeLeser = (id: string, leser: string[]) =>
  hole<{ id: string; leser: string[] }>('report-leser', { method: 'POST', body: { id, leser } });

// ─── Simulationsmodelle ──────────────────────────────────────────────────────

/** Eine benannte Jury — Marktset in fester Fassung, läuft auf Claude.
 *  `quote` und `intervall` sind unter zehn entscheidbaren Ergebnissen null,
 *  auch im JSON: eine Zahl, die der Server nicht behauptet, zeigt die App nicht. */
export interface Modellkarte {
  slug: string;
  name: string;
  kurz: string;
  beschreibung: string | null;
  set: string;
  fassung: number | null;
  sprachmodell: string;
  personas: number;
  trefferquote: {
    gemessen: boolean;
    n: number;
    treffer: number;
    quote: number | null;
    intervall: [number, number] | null;
    besser_als_zufall: boolean;
    satz: string;
  };
  hinweis: string;
}

export async function ladeModelle(): Promise<Modellkarte[]> {
  return (await hole<{ models: Modellkarte[] }>('models')).models ?? [];
}

// ─── Marktergebnis und Trefferquote ──────────────────────────────────────────

export interface ErgebnisMeldung {
  run: string;
  gewinner: 'kandidat' | 'baseline' | 'unentschieden';
  /** Nur experiment und split_test zählen (trefferquote.ts, #311). */
  testaufbau: 'experiment' | 'split_test' | 'rsa_assets' | 'vorher_nachher' | 'unbekannt';
  metrik: string;
  kandidat_zaehler?: number;
  kandidat_nenner?: number;
  baseline_zaehler?: number;
  baseline_nenner?: number;
  zeitraum?: string;
}

export interface Quote {
  n: number;
  treffer: number;
  quote: number | null;
  intervall: [number, number] | null;
  besser_als_zufall: boolean;
  nicht_zaehlbar: number;
  satz: string;
}

/** Dieselbe Funktion wie `report_outcome` in Claude — dieselben Regeln, was zählt. */
export const meldeErgebnis = (m: ErgebnisMeldung) =>
  hole<{ zaehlbar: boolean; treffer: boolean | null; grund: string; trefferquote: Quote }>('outcome', { method: 'POST', body: m });

/**
 * Seit 26.09.2026 je Art der Zielgruppe (trefferquote.ts): `eigene` gilt für
 * Firmenkunden wie bisher, `weitere` nennt Privatkunden und Politik getrennt,
 * weil die Jury dort mit einem anderen Prompt urteilt. Vor dem Deploy fehlt
 * `weitere`.
 */
export interface Trefferquoten {
  eigene: Quote;
  global: Quote | null;
  zielgruppe_art?: 'b2b' | 'b2c' | 'politik';
  weitere?: Array<{ zielgruppe_art: 'b2b' | 'b2c' | 'politik'; eigene: Quote }>;
}

export const ladeQuote = () => hole<Trefferquoten>('hit-rate');
